const NAVIGATION_STATE_KEY = "verifiedCatalogueDetail";
export const CATALOGUE_DETAIL_SESSION_KEY = "cc.public-catalogue.detail-navigation.v1";

const SNAPSHOT_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/;
const SLUG_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._~-]{0,255}$/;

const normalise = (value) => String(value ?? "").trim();

const validSnapshotId = (value) => {
  const clean = normalise(value);
  return SNAPSHOT_ID_PATTERN.test(clean) ? clean : null;
};

const validSlug = (value) => {
  const clean = normalise(value);
  return SLUG_PATTERN.test(clean) ? clean : null;
};

const sessionStorageOrNull = (storage) => {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const navigationToken = () => {
  try {
    if (typeof globalThis.crypto?.randomUUID === "function") {
      return globalThis.crypto.randomUUID();
    }
  } catch {
    // Fall through to a non-security token. This token only binds two public
    // pieces of browser state; the API remains the snapshot authority.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const validHandoff = (value, expectedSlug) => {
  if (!value || value.version !== 1) return null;
  const snapshotId = validSnapshotId(value.snapshot_id);
  const slug = validSlug(value.slug);
  const token = normalise(value.token);
  if (!snapshotId || !slug || slug !== validSlug(expectedSlug) || !token || token.length > 256) {
    return null;
  }
  return {
    version: 1,
    snapshot_id: snapshotId,
    slug,
    token,
  };
};

const storedHandoff = (storage, expectedSlug) => {
  const target = sessionStorageOrNull(storage);
  if (!target) return { available: false, handoff: null };
  try {
    return {
      available: true,
      handoff: validHandoff(
        JSON.parse(target.getItem(CATALOGUE_DETAIL_SESSION_KEY) || "null"),
        expectedSlug,
      ),
    };
  } catch {
    return { available: true, handoff: null };
  }
};

export const createCatalogueDetailNavigationState = ({
  snapshotId,
  slug,
  storage,
  token = navigationToken(),
} = {}) => {
  const handoff = validHandoff({
    version: 1,
    snapshot_id: snapshotId,
    slug,
    token,
  }, slug);
  if (!handoff) return null;

  const target = sessionStorageOrNull(storage);
  try {
    target?.setItem(CATALOGUE_DETAIL_SESSION_KEY, JSON.stringify(handoff));
  } catch {
    // React Router state still carries the public snapshot ID. The detail API
    // independently rejects any snapshot that is not an approved retained set.
  }
  return { [NAVIGATION_STATE_KEY]: handoff };
};

export const catalogueDetailSnapshotFromNavigation = ({
  state,
  slug,
  storage,
} = {}) => {
  const routeHandoff = validHandoff(state?.[NAVIGATION_STATE_KEY], slug);
  if (!routeHandoff) return null;

  const stored = storedHandoff(storage, slug);
  if (!stored.available) return routeHandoff.snapshot_id;
  if (
    !stored.handoff
    || stored.handoff.token !== routeHandoff.token
    || stored.handoff.snapshot_id !== routeHandoff.snapshot_id
  ) {
    return null;
  }
  return routeHandoff.snapshot_id;
};

export const clearCatalogueDetailNavigation = ({ state, slug, storage } = {}) => {
  const routeHandoff = validHandoff(state?.[NAVIGATION_STATE_KEY], slug);
  if (!routeHandoff) return;

  const target = sessionStorageOrNull(storage);
  try {
    const stored = storedHandoff(target, slug).handoff;
    if (stored?.token === routeHandoff.token) {
      target?.removeItem(CATALOGUE_DETAIL_SESSION_KEY);
    }
  } catch {
    // A blocked session store must not block a public detail request.
  }

  if (typeof window === "undefined") return;
  try {
    const current = window.history.state;
    const userState = current?.usr;
    if (userState?.[NAVIGATION_STATE_KEY]?.token !== routeHandoff.token) return;
    const nextUserState = { ...userState };
    delete nextUserState[NAVIGATION_STATE_KEY];
    window.history.replaceState(
      {
        ...current,
        usr: Object.keys(nextUserState).length ? nextUserState : null,
      },
      "",
    );
  } catch {
    // The matching session handoff was already consumed, so refreshes still
    // fall back to the current snapshot even if history replacement is blocked.
  }
};
